import { getApiClient } from '../../api/client';
import type { components } from '../../api/schema';

export type ChainSearchCreate = components['schemas']['ChainSearchRequestCreate'];
export type ChainSearchRead = components['schemas']['ChainSearchRequestRead'];
export type ChainSearchDetail = components['schemas']['ChainSearchRequestDetailRead'];
export type ChainSearchCandidate = components['schemas']['ChainSearchCandidateRead'];
export type ChainRead = components['schemas']['ChainRead'];
export type ChainAggregate = components['schemas']['ChainAggregateRead'];

export async function createChainSearch(body: ChainSearchCreate) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/searches', { body });
  if (error) throw new Error(`Failed to create chain search: ${JSON.stringify(error)}`);
  return data;
}

export async function listChainSearches(page = 1, limit = 50) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/searches', {
    params: { query: { page, limit } },
  });
  if (error) throw new Error(`Failed to list chain searches: ${JSON.stringify(error)}`);
  return data;
}

/** Server-side filters for the Chains list table. */
export type ChainListFilters = {
  q?: string;
  business_type?: string;
  industry?: string;
};

export async function listChains(page = 1, limit = 50, filters: ChainListFilters = {}) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains', {
    params: {
      query: {
        page,
        limit,
        q: filters.q || undefined,
        business_type: filters.business_type || undefined,
        industry: filters.industry || undefined,
      },
    },
  });
  if (error) throw new Error(`Failed to list chains: ${JSON.stringify(error)}`);
  return data;
}

export async function getChainSearch(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/searches/{uuid}', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch chain search: ${JSON.stringify(error)}`);
  return data;
}

export async function selectChainCandidates(uuid: string, selected_place_ids: string[]) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/searches/{uuid}/select', {
    params: { path: { uuid } },
    body: { selected_place_ids },
  });
  if (error) throw new Error(`Failed to update selection: ${JSON.stringify(error)}`);
  return data;
}

export async function createChainScorecards(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/searches/{uuid}/create-scorecards', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to create scorecards: ${JSON.stringify(error)}`);
  return data;
}

export async function cancelChainSearch(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/searches/{uuid}/cancel', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to cancel chain search: ${JSON.stringify(error)}`);
  return data;
}

export async function retryChainSearch(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/searches/{uuid}/retry', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to retry chain search: ${JSON.stringify(error)}`);
  return data;
}

export async function getChainAggregate(chain_uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/{chain_uuid}', {
    params: { path: { chain_uuid } },
  });
  if (error) throw new Error(`Failed to fetch chain: ${JSON.stringify(error)}`);
  return data;
}

export type ChainSuggestedLink = components['schemas']['ChainSuggestedLinkRead'];
export type ChainPlace = components['schemas']['ChainPlaceRead'];
export type ChainUpdateRequest = components['schemas']['ChainUpdateRequest'];
export type ChainLocationAudit = components['schemas']['ChainLocationAuditRead'];

/** Server-side filters for a chain's Locations table. */
export type ChainPlaceFilters = {
  business_type?: string;
  industry?: string;
  business_status?: string;
  min_google_rating?: number;
  max_google_rating?: number;
  source?: string;
};

/**
 * List a chain's member locations in the rich `PlaceSmallRead` shape (each
 * place with its `reports`→`periods`, `tasks`, `contacts`) plus the integer
 * Place id used by the location-management endpoints. One row per location;
 * the detail page renders the latest scorecard info per row using the same
 * complex columns as the main /dashboard/scorecard table.
 */
export async function listChainPlaces(
  chain_uuid: string,
  page = 1,
  limit = 100,
  q?: string,
  filters: ChainPlaceFilters = {},
) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/{chain_uuid}/places', {
    params: {
      path: { chain_uuid },
      query: {
        page,
        limit,
        q,
        business_type: filters.business_type || undefined,
        industry: filters.industry || undefined,
        business_status: filters.business_status || undefined,
        min_google_rating: filters.min_google_rating,
        max_google_rating: filters.max_google_rating,
        source: filters.source || undefined,
      },
    },
  });
  if (error) throw new Error(`Failed to list chain places: ${JSON.stringify(error)}`);
  return data;
}

/** Rename a chain / override its slug without touching locations. */
export async function updateChain(chain_uuid: string, body: ChainUpdateRequest) {
  const client = getApiClient();
  const { data, error } = await client.PATCH('/chains/{chain_uuid}', {
    params: { path: { chain_uuid } },
    body,
  });
  if (error) throw new Error(`Failed to update chain: ${JSON.stringify(error)}`);
  return data;
}

/** Move or remove one location's chain membership without a full rerun. */
export async function updateChainPlaceMembership(
  chain_uuid: string,
  place_id: number,
  body: { chain_uuid?: string | null; reason?: string | null; force?: boolean },
) {
  const client = getApiClient();
  const { data, error } = await client.PATCH(
    '/chains/{chain_uuid}/places/{place_id}/membership',
    {
      params: { path: { chain_uuid, place_id } },
      body: { ...body, force: body.force ?? false },
    },
  );
  if (error) throw new Error(`Failed to update membership: ${JSON.stringify(error)}`);
  return data;
}

/** Audit trail of add/link/move/unlink membership changes for a chain. */
export async function listChainLocationAudit(chain_uuid: string, limit = 100) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/{chain_uuid}/location-audit', {
    params: { path: { chain_uuid }, query: { limit } },
  });
  if (error) throw new Error(`Failed to list location audit: ${JSON.stringify(error)}`);
  return data;
}

export async function listChainSuggestedLinks(chain_uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/{chain_uuid}/suggested-links', {
    params: { path: { chain_uuid } },
  });
  if (error) throw new Error(`Failed to list suggested links: ${JSON.stringify(error)}`);
  return data;
}

export async function linkChainPlaces(chain_uuid: string, place_ids: number[]) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/{chain_uuid}/link-places', {
    params: { path: { chain_uuid } },
    body: { place_ids },
  });
  if (error) throw new Error(`Failed to link places: ${JSON.stringify(error)}`);
  return data;
}

export async function unlinkChainPlaces(chain_uuid: string, place_ids: number[]) {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/{chain_uuid}/unlink-places', {
    params: { path: { chain_uuid } },
    body: { place_ids },
  });
  if (error) throw new Error(`Failed to unlink places: ${JSON.stringify(error)}`);
  return data;
}

// ---- Chain scorecard reports ----

export type ChainReportCreate = components['schemas']['CreateChainReportRequest'];
export type ChainReportCreateResponse = components['schemas']['CreateChainReportResponse'];
export type ChainReport = components['schemas']['ChainReportRead'];
export type ChainPeriodReport = components['schemas']['ChainPeriodReportRead'];
export type ChainReportSourceSnapshot =
  components['schemas']['ChainReportSourceSnapshotRead'];

export async function createChainReport(
  chain_uuid: string,
  body: ChainReportCreate,
): Promise<ChainReportCreateResponse | undefined> {
  const client = getApiClient();
  const { data, error } = await client.POST('/chains/{chain_uuid}/reports', {
    params: { path: { chain_uuid } },
    body,
  });
  if (error) throw new Error(`Failed to create chain scorecard: ${JSON.stringify(error)}`);
  return data;
}

export async function listChainReports(chain_uuid: string, page = 1, limit = 50) {
  const client = getApiClient();
  const { data, error } = await client.GET('/chains/{chain_uuid}/reports', {
    params: { path: { chain_uuid }, query: { page, limit } },
  });
  if (error) throw new Error(`Failed to list chain scorecards: ${JSON.stringify(error)}`);
  return data;
}

/**
 * Download a chain scorecard PDF. Unauth — mirrors `downloadReportPdf`
 * for per-place reports. Returns an object URL the caller can stick on
 * an `<a download>` element.
 */
export async function downloadChainReportPdf(uuid: string, source: string, period: string) {
  const { publicClient } = await import('../../api/client');
  const { data, error } = await publicClient.GET(
    '/chains/reports/{uuid}/{source}/{period_id}/pdf',
    {
      params: { path: { uuid, source, period_id: period } },
      parseAs: 'blob',
    },
  );
  if (error) throw new Error(`Failed to download chain scorecard PDF: ${JSON.stringify(error)}`);
  return URL.createObjectURL(data as Blob);
}

/**
 * Fetch a chain scorecard by UUID or human-readable slug. Unauth — URL
 * knowledge is the share key, same pattern as `getReport(uuid_or_slug)`
 * for place scorecards.
 */
export async function getChainReport(uuidOrSlug: string) {
  const { publicClient } = await import('../../api/client');
  const { data, error } = await publicClient.GET('/chains/reports/{uuid_or_slug}', {
    params: { path: { uuid_or_slug: uuidOrSlug } },
  });
  if (error) throw new Error(`Failed to fetch chain scorecard: ${JSON.stringify(error)}`);
  return data;
}


export async function hideChainReport(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/chains/reports/{uuid}/hide', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to hide chain scorecard: ${JSON.stringify(error)}`);
}

export async function unhideChainReport(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/chains/reports/{uuid}/unhide', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to unhide chain scorecard: ${JSON.stringify(error)}`);
}

export const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'D.C.' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];
